// src/app/modules/adminrh/parametres/ParamJT/ParamJT.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {Plus, X, Calendar, FolderKanban} from "lucide-react";
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import "../parametresStyles/ParamJT.css";
import ProjetsTab from "./ProjetsTab";

import {
    getWorkdaysConfigRequest,
    updateWorkdaysConfigRequest,
    getCalendarSettingsRequest,
    updateCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";

const WEEKDAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];

// âœ… Normalise backend -> UI
const normalizeWorkDays = (input) => {
    const out = {};
    WEEKDAYS.forEach((d) => {
        const v = input?.[d] || {};
        const isWorking =
            typeof v.isWorking === "boolean"
                ? v.isWorking
                : typeof v.working === "boolean"
                    ? v.working
                    : !(d === "samedi" || d === "dimanche");

        const hoursRaw = Number(v.hours ?? (isWorking ? 8 : 0));
        const hours = Number.isFinite(hoursRaw) ? hoursRaw : isWorking ? 8 : 0;

        out[d] = { isWorking, hours: isWorking ? hours : 0 };
    });
    return out;
};

// âœ… UI -> backend
const toBackendWorkDays = (uiWorkDays) => {
    const out = {};
    WEEKDAYS.forEach((d) => {
        const v = uiWorkDays?.[d] || {};
        const isWorking = !!v.isWorking;
        const hoursRaw = Number(v.hours ?? (isWorking ? 8 : 0));
        const hours = Number.isFinite(hoursRaw) ? hoursRaw : isWorking ? 8 : 0;

        out[d] = {
            hours: isWorking ? hours : 0,
            working: isWorking,
        };
    });
    return out;
};

export default function ParamJT() {
    const dispatch = useDispatch();

    const { workdaysConfig, calendarSettings, loading, error } = useSelector((state) => state.workDays || {});

    // =========================
    // LOCAL STATE
    // =========================
    const [activeTab, setActiveTab] = useState("calendrier");

    const [workDays, setWorkDays] = useState({
        lundi:    { isWorking: true,  hours: 8 },
        mardi:    { isWorking: true,  hours: 8 },
        mercredi: { isWorking: true,  hours: 8 },
        jeudi:    { isWorking: true,  hours: 8 },
        vendredi: { isWorking: true,  hours: 8 },
        samedi:   { isWorking: false, hours: 0 },
        dimanche: { isWorking: false, hours: 0 },
    });

    const [weekendStart, setWeekendStart] = useState("samedi");

    const [fixedHolidays, setFixedHolidays]   = useState([]);
    const [hijriHolidays, setHijriHolidays]   = useState([]);
    const [customHolidays, setCustomHolidays] = useState([]);

    const [selectedDate, setSelectedDate]           = useState("");
    const [customHolidayDesc, setCustomHolidayDesc] = useState("");

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const currentYear = currentMonth.getFullYear();

    // =========================
    // LOAD
    // =========================
    useEffect(() => {
        dispatch(getWorkdaysConfigRequest());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(getCalendarSettingsRequest(currentYear, "MA"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentYear]);

    // =========================
    // HYDRATE FROM REDUX
    // =========================
    useEffect(() => {
        if (!workdaysConfig) return;
        if (workdaysConfig.weekendStart) setWeekendStart(workdaysConfig.weekendStart);
        if (workdaysConfig.workDays)     setWorkDays(normalizeWorkDays(workdaysConfig.workDays));
    }, [workdaysConfig]);

    useEffect(() => {
        if (!calendarSettings) return;

        setFixedHolidays(
            (calendarSettings.fixed || []).map((x) => ({
                date: x.key,
                description: x.label,
                selected: !!x.selected,
            }))
        );

        setHijriHolidays(
            (calendarSettings.hijri || []).map((x) => ({
                name: x.name,
                date: x.date || "",
                duration: Number(x.duration || 1),
                selected: !!x.selected,
            }))
        );

        setCustomHolidays(
            (calendarSettings.custom || []).map((x) => ({
                date: x.date,
                description: x.description,
            }))
        );
    }, [calendarSettings]);

    // =========================
    // WEEKEND AUTO
    // =========================
    useEffect(() => {
        const weekendIndex = WEEKDAYS.indexOf(weekendStart);
        if (weekendIndex === -1) return;

        const nextDay = WEEKDAYS[(weekendIndex + 1) % 7];

        setWorkDays((prev) => {
            const copy = { ...prev };
            copy[weekendStart] = { isWorking: false, hours: 0 };
            copy[nextDay]      = { isWorking: false, hours: 0 };
            return copy;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekendStart]);

    // =========================
    // HANDLERS
    // =========================
    const handleWorkDayChange = (day, field, value) => {
        setWorkDays((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: field === "hours" ? Number(value) : value,
                ...(field === "isWorking" && !value && { hours: 0 }),
            },
        }));
    };

    const toggleFixedHoliday = (index) => {
        setFixedHolidays((prev) => prev.map((h, i) => (i === index ? { ...h, selected: !h.selected } : h)));
    };

    const toggleHijriHoliday = (index) => {
        setHijriHolidays((prev) => prev.map((h, i) => (i === index ? { ...h, selected: !h.selected } : h)));
    };

    const updateHijriHolidayDate = (index, date) => {
        setHijriHolidays((prev) => prev.map((h, i) => (i === index ? { ...h, date } : h)));
    };

    const updateHijriHolidayDuration = (index, duration) => {
        setHijriHolidays((prev) => prev.map((h, i) => (i === index ? { ...h, duration: Number(duration) } : h)));
    };

    const handleAddCustomHoliday = () => {
        if (!selectedDate || !customHolidayDesc) return;
        const formattedDate = new Date(selectedDate).toISOString().split("T")[0];
        if (!customHolidays.some((h) => h.date === formattedDate)) {
            setCustomHolidays((prev) => [...prev, { date: formattedDate, description: customHolidayDesc }]);
        }
        setSelectedDate("");
        setCustomHolidayDesc("");
    };

    const removeCustomHoliday = (index) => {
        setCustomHolidays((prev) => prev.filter((_, i) => i !== index));
    };

    // =========================
    // CALENDAR HELPERS
    // =========================
    const isFixedHoliday = (date) => {
        if (!date) return false;
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day   = String(date.getDate()).padStart(2, "0");
        return fixedHolidays.some((h) => h.date === `${month}-${day}` && h.selected);
    };

    const isHijriHoliday = (date) => {
        if (!date) return false;
        return hijriHolidays.some((h) => {
            if (!h.selected || !h.date) return false;
            const holidayDate = new Date(h.date);
            const checkDate   = new Date(date);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);
            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);
                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
    };

    const isCustomHoliday = (date) => {
        if (!date) return false;
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return customHolidays.some((h) => h.date === `${y}-${m}-${d}`);
    };

    const getWorkInfo = (date) => {
        if (!date) return { isWorking: false, hours: 0, dayName: "" };
        let idx = date.getDay();
        idx = idx === 0 ? 6 : idx - 1;
        const dayName = WEEKDAYS[idx];
        const cfg = workDays?.[dayName];
        const isWorking =
            cfg && typeof cfg.isWorking === "boolean"
                ? cfg.isWorking
                : !(dayName === "samedi" || dayName === "dimanche");
        const hours = cfg ? Number(cfg.hours || 0) : isWorking ? 8 : 0;
        return { isWorking, hours: isWorking ? hours : 0, dayName };
    };

    const isNonWorkingDay = (date) => {
        if (!date) return false;
        return !getWorkInfo(date).isWorking;
    };

    const getHolidayName = (date) => {
        if (!date) return "";
        const names = [];

        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day   = String(date.getDate()).padStart(2, "0");
        const fixed = fixedHolidays.find((h) => h.date === `${month}-${day}` && h.selected);
        if (fixed) names.push(fixed.description);

        const year  = date.getFullYear();
        const m2    = String(date.getMonth() + 1).padStart(2, "0");
        const d2    = String(date.getDate()).padStart(2, "0");
        const fullDate = `${year}-${m2}-${d2}`;

        hijriHolidays.forEach((h) => {
            if (!h.selected || !h.date) return;
            const holidayDate = new Date(h.date);
            holidayDate.setHours(0, 0, 0, 0);
            for (let i = 0; i < (h.duration || 1); i++) {
                const test = new Date(holidayDate);
                test.setDate(test.getDate() + i);
                test.setHours(0, 0, 0, 0);
                const testStr = `${test.getFullYear()}-${String(test.getMonth() + 1).padStart(2, "0")}-${String(test.getDate()).padStart(2, "0")}`;
                if (testStr === fullDate) {
                    names.push(`${h.name} (Jour ${i + 1}/${h.duration || 1})`);
                    break;
                }
            }
        });

        const custom = customHolidays.find((h) => h.date === fullDate);
        if (custom) names.push(custom.description);

        return names.join(" â€¢ ");
    };

    const getMonthData = (date) => {
        const y = date.getFullYear();
        const m = date.getMonth();
        const firstDay = new Date(y, m, 1);
        const lastDay  = new Date(y, m + 1, 0);

        let startOffset = firstDay.getDay() - 1;
        if (startOffset === -1) startOffset = 6;

        const daysInMonth = lastDay.getDate();
        const weeks = [];
        let currentWeek = [];

        for (let i = 0; i < startOffset; i++) currentWeek.push(null);

        for (let d = 1; d <= daysInMonth; d++) {
            if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
            currentWeek.push(new Date(y, m, d));
        }
        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
        return weeks;
    };

    const dayClass = (date) => {
        if (!date)               return "jtDay jtDay--empty";
        if (isFixedHoliday(date))  return "jtDay jtDay--fixed";
        if (isHijriHoliday(date))  return "jtDay jtDay--hijri";
        if (isCustomHoliday(date)) return "jtDay jtDay--custom";
        if (isNonWorkingDay(date)) return "jtDay jtDay--weekend";
        return "jtDay";
    };

    const monthLabel = useMemo(() =>
            currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        [currentMonth]);

    // =========================
    // SAVE
    // =========================
    const handleSave = () => {
        // âœ… Calcul automatique depuis les jours de travail déjà saisis
        const heuresMaxParJour = Math.max(
            ...Object.values(workDays)
                .filter((d) => d.isWorking)
                .map((d) => Number(d.hours) || 0),
            0
        );

        const heuresParJourOuvre = heuresMaxParJour; // même valeur, ou tu peux faire une moyenne

        dispatch(updateWorkdaysConfigRequest({
            weekendStart,
            workDays: toBackendWorkDays(workDays),
            // âœ… dérivés automatiquement, plus besoin de saisie supplémentaire
            heuresMaxParJour,
            heuresParJourOuvre,
            heuresSuppAutorisees: false,   // valeur par défaut raisonnable
            modesAutorises: "PRESENTIEL",  // adapte selon ton enum TimesheetMode
            tauxRemplissageMinimum: 80,    // adapte selon ta logique métier
        }));

        dispatch(
            updateCalendarSettingsRequest(currentYear, "MA", {
                fixed: fixedHolidays.map((h) => ({
                    key:      h.date,
                    label:    h.description,
                    selected: !!h.selected,
                })),
                hijri: hijriHolidays.map((h) => ({
                    name:     h.name,
                    date:     h.date || "",
                    duration: Number(h.duration || 1),
                    selected: !!h.selected,
                })),
                custom: customHolidays.map((h) => ({
                    date:        h.date,
                    description: h.description,
                })),
            })
        );
    };

    // =========================
    // UI
    // =========================
    return (
        <div className="jtPage">
            <div className="jtHeader">
                <div className="jtHeader__left">
                    <div>
                        <h1>Gestion des jours de travail</h1>
                        <p>Weekend, heures de travail, jours fériés et projets</p>
                        {error  ? <div className="jtError">{String(error)}</div> : null}
                        {loading ? <div className="jtHint">Chargement…</div> : null}
                    </div>
                </div>
                {activeTab === "calendrier" && (
                    <button className="jtBtn jtBtn--primary" onClick={handleSave} disabled={!!loading}>
                        Enregistrer
                    </button>
                )}
            </div>

            <div className="jtTabs">
                <button 
                    className={`jtTab ${activeTab === "calendrier" ? "is-active" : ""}`} 
                    onClick={() => setActiveTab("calendrier")}
                >
                    <Calendar size={14} style={{ marginRight: 6 }} /> Jours de travail & Fériés
                </button>
                <button 
                    className={`jtTab ${activeTab === "projets" ? "is-active" : ""}`} 
                    onClick={() => setActiveTab("projets")}
                >
                    <FolderKanban size={14} style={{ marginRight: 6 }} /> Projets
                </button>
            </div>

            {activeTab === "projets" && <ProjetsTab />}

            {activeTab === "calendrier" && (
                <>
                    <div className="jtGrid">
                {/* ── JOURS DE TRAVAIL ── */}
                <div className="jtCard">
                    <div className="jtCard__title">JOURS DE TRAVAIL</div>
                    <div className="jtScroll">
                        {WEEKDAYS.map((day) => {
                            const info = workDays[day] || { isWorking: false, hours: 0 };
                            return (
                                <div key={day} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                                    <label className="jtLine" style={{ textTransform: "capitalize", margin: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={info.isWorking}
                                            onChange={(e) => handleWorkDayChange(day, "isWorking", e.target.checked)}
                                        />
                                        <span>{day}</span>
                                    </label>
                                    {info.isWorking && (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                className="jtDur"
                                                type="number"
                                                min="1"
                                                max="24"
                                                value={info.hours}
                                                onChange={(e) => handleWorkDayChange(day, "hours", e.target.value)}
                                                style={{ width: "60px", marginRight: "6px", textAlign: "center" }}
                                            />
                                            <span>h</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── FÉRIÉS FIXES ── */}
                <div className="jtCard">
                    <div className="jtCard__title">FÉRIÉS FIXES</div>
                    <div className="jtScroll">
                        {fixedHolidays.map((h, idx) => (
                            <label className="jtLine" key={`${h.date}-${idx}`}>
                                <input type="checkbox" checked={!!h.selected} onChange={() => toggleFixedHoliday(idx)} />
                                <span>{h.description} {h.date && `(${h.date.split("-").reverse().join("/")})`}</span>
                            </label>
                        ))}
                        {fixedHolidays.length === 0 && (
                            <div className="jtHint">Aucun férié fixe (selon pays/année)</div>
                        )}
                    </div>
                </div>

                {/* â”€â”€ FÉRIÉS HIJRIS â”€â”€ */}
                <div className="jtCard">
                    <div className="jtCard__title">FÉRIÉS HIJRIS</div>
                    <div className="jtScroll">
                        {hijriHolidays.map((h, idx) => (
                            <div className="jtHijriRow" key={`${h.name}-${idx}`}>
                                <label className="jtLine">
                                    <input type="checkbox" checked={!!h.selected} onChange={() => toggleHijriHoliday(idx)} />
                                    <span>{h.name}</span>
                                </label>
                                {h.selected && (
                                    <div className="jtHijriInputs">
                                        <input
                                            className="jtDate"
                                            type="date"
                                            value={h.date || ""}
                                            onChange={(e) => updateHijriHolidayDate(idx, e.target.value)}
                                        />
                                        <div className="jtDuration">
                                            <span>Durée</span>
                                            <input
                                                className="jtDur"
                                                type="number"
                                                min="1"
                                                max="5"
                                                value={Number(h.duration || 1)}
                                                onChange={(e) => updateHijriHolidayDuration(idx, e.target.value)}
                                            />
                                            <span>jour(s)</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {hijriHolidays.length === 0 && (
                            <div className="jtHint">Aucun férié Hijri (selon pays/année)</div>
                        )}
                    </div>
                </div>

            </div>

            {/* â”€â”€ CALENDRIER â”€â”€ */}
            <div className="jtCalendarCard">
                <div className="jtCalHeader">
                    <button
                        className="jtNavBtn"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                    >
                        <ArrowBack/>
                    </button>
                    <div className="jtMonth">{monthLabel}</div>
                    <button
                        className="jtNavBtn"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                    >
                        <ArrowForward/>
                    </button>
                </div>

                <div className="jtTable">
                    <div className="jtHeadRow">
                        {WEEKDAYS_SHORT.map((d, i) => (
                            <div className="jtHeadCell" key={i}>{d}</div>
                        ))}
                    </div>
                    {getMonthData(currentMonth).map((week, wIdx) => (
                        <div className="jtRow" key={wIdx}>
                            {week.map((date, dIdx) => (
                                <div
                                    className={dayClass(date)}
                                    key={dIdx}
                                    title={date ? getHolidayName(date) : ""}
                                >
                                    {date ? <span className="jtDayNum">{date.getDate()}</span> : null}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="jtLegend">
                    <div className="jtLegendItem"><span className="jtDot jtDot--weekend" /> Non ouvré</div>
                    <div className="jtLegendItem"><span className="jtDot jtDot--fixed"   /> Férié fixe</div>
                    <div className="jtLegendItem"><span className="jtDot jtDot--hijri"   /> Férié Hijri</div>
                    <div className="jtLegendItem"><span className="jtDot jtDot--custom"  /> Autre férié</div>
                </div>
            </div>
            </>
            )}
        </div>
    );
}
